import { composeComponentTags } from "./composeTags";
import type { ComposeNode, ComposePlan } from "./types";

let _seq = 0;
function nextCloneId(): string {
  _seq += 1;
  return `cmp-split-${_seq}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Test helper. */
export function resetSplitIdSequence(): void {
  _seq = 0;
}

function withStyleIdentity(node: ComposeNode, styleKey: string): ComposeNode {
  const styleUpper = styleKey.toUpperCase().replace(/[^A-Z0-9]+/g, "-");
  const codeHasStyle = node.code.toUpperCase().includes(styleUpper);
  const nameHasStyle = node.name.toLowerCase().includes(styleKey.toLowerCase());
  const fillSlug =
    node.tags.find((t) => t.startsWith("fill:"))?.slice("fill:".length) ?? null;
  return {
    ...node,
    styleKey,
    code: codeHasStyle ? node.code : `${node.code}-${styleUpper}`,
    name: nameHasStyle ? node.name : `${node.name} ${styleKey}`,
    tags: composeComponentTags({
      sizeLabel: node.sizeLabel,
      styleKey,
      slotKey: node.slotKey ?? "slot",
      variantKey: node.variantKey,
      fillSlug,
    }),
  };
}

export type SplitComponentByStyleResult =
  | { ok: true; plan: ComposePlan; created: number; styles: string[] }
  | { ok: false; error: string };

/**
 * When one component is shared across styles (styleSpecific was false),
 * clone it per style among unfinished product parents and rewire slots
 * so each style can be matched independently. Marks the archetype slot
 * styleSpecific for later expands.
 */
export function splitComponentByStyle(
  plan: ComposePlan,
  componentNodeId: string,
): SplitComponentByStyleResult {
  const source = plan.nodes.find((n) => n.id === componentNodeId);
  if (!source || source.role !== "component") {
    return { ok: false, error: "Select a planned component slot to split." };
  }

  const unfinishedParents = plan.nodes.filter(
    (n) =>
      n.role === "product" &&
      n.match.status !== "matched" &&
      n.slots.some((s) => s.childNodeId === componentNodeId),
  );
  if (unfinishedParents.length === 0) {
    return { ok: false, error: "No unfinished products still use this component." };
  }

  const byStyle = new Map<string, ComposeNode[]>();
  for (const p of unfinishedParents) {
    const style = (p.styleKey ?? "").trim();
    if (!style) continue;
    const list = byStyle.get(style) ?? [];
    list.push(p);
    byStyle.set(style, list);
  }
  const styles = [...byStyle.keys()];
  if (styles.length < 2) {
    return {
      ok: false,
      error:
        "This part is only used by one style among unfinished SKUs — nothing to split.",
    };
  }

  const keepStyle = styles[0]!;
  const clones: ComposeNode[] = [];
  const styleToNodeId = new Map<string, string>();
  styleToNodeId.set(keepStyle, source.id);

  for (const style of styles.slice(1)) {
    const clone: ComposeNode = {
      ...withStyleIdentity(source, style),
      id: nextCloneId(),
      match: { status: "unmatched" },
    };
    clones.push(clone);
    styleToNodeId.set(style, clone.id);
  }

  const kept = withStyleIdentity(source, keepStyle);
  const parentIds = new Set(unfinishedParents.map((p) => p.id));

  const nextNodes = plan.nodes.map((n) => {
    if (n.id === source.id) return kept;
    if (!parentIds.has(n.id) || n.role !== "product") return n;
    const style = (n.styleKey ?? "").trim();
    const targetId = styleToNodeId.get(style);
    if (!targetId) return n;
    return {
      ...n,
      slots: n.slots.map((s) =>
        s.childNodeId === componentNodeId ? { ...s, childNodeId: targetId } : s,
      ),
    };
  });

  const slotKey = source.slotKey;
  const nextSlots = plan.archetype.slots.map((s) =>
    slotKey && s.key === slotKey ? { ...s, styleSpecific: true } : s,
  );

  return {
    ok: true,
    created: clones.length,
    styles,
    plan: {
      ...plan,
      archetype: { ...plan.archetype, slots: nextSlots },
      nodes: [...nextNodes, ...clones],
      updatedAt: new Date().toISOString(),
    },
  };
}
